import { type AdditionalProductDetails, type HttpResponse, type ProductModelV2, getAdditionalProductDetails, getCanonicalProductDetailsV2, getPharmacyProductDetailsV2 } from '@develop/ia-api-library'

type ProductDetailsResponse = {
  _meta: Omit<HttpResponse<ProductModelV2>, 'data'>
  data: ProductModelV2
}

export const useProductDetails = async (_pzn: MaybeRefOrGetter<string>, opts?: {
  pharmacyId?: MaybeRefOrGetter<string | number | undefined>
  immediate?: boolean // not really needed when getCachedDetails is used, no new request will be made automatically
  lazy?: boolean
  server?: boolean
  deep?: boolean
}) => {
  const { pharmacyId: currentPharmacyId } = storeToRefs(useCurrentPharmacyStore())
  const pharmacyId = computed(() => isDefined(opts?.pharmacyId) ? toValue(opts.pharmacyId) : currentPharmacyId.value)

  const pzn = computed(() => toValue(_pzn))

  const key = computed(() => constructProductKey({
    pzn: pzn.value,
    pharmacyId: pharmacyId.value,
  }))

  const { data: cachedProductDetails } = useNuxtData<ProductDetailsResponse>(key.value)

  const { data: productDetailsRes, ...rest } = await useAsyncData(key.value, async () => {
    const productBaseDetailsRes = pharmacyId.value
      ? await getPharmacyProductDetailsV2(pzn.value, pharmacyId.value)
      : await getCanonicalProductDetailsV2(pzn.value)

    if (!productBaseDetailsRes.success) {
      throw new Error(productBaseDetailsRes.message ?? 'Error fetching product details')
    }

    const { data: productBaseDetails, ...productDetailsMeta } = productBaseDetailsRes

    return {
      _meta: productDetailsMeta,
      data: productBaseDetails as ProductModelV2,
    }
  }, {
    immediate: opts?.immediate ?? true,
    lazy: opts?.lazy ?? false,
    server: opts?.server ?? true,
    deep: opts?.deep ?? false,
    dedupe: 'cancel',
    getCachedData: getCachedData<ProductDetailsResponse>,
  })

  const meta = computed(() => productDetailsRes.value?._meta)
  const productDetails = computed(() => productDetailsRes.value?.data as ProductModelV2)

  const clearCachedDetails = () => clearNuxtData(key.value)
  const refreshCachedDetails = () => refreshNuxtData(key.value)

  return {
    productDetails,
    // => not really needed, in case we pass immediate=false (and not already use getCachedData),
    // returned data should already be cached one by default (if already fetched)
    cachedProductDetails,
    clearCachedDetails,
    refreshCachedDetails,
    meta,
    ...rest,
  }
}

export const useAdditionalProductDetails = async (_pzn: MaybeRefOrGetter<string>, opts?: {
  immediate?: boolean // not really needed when getCachedDetails is used, no new request will be made automatically
  lazy?: boolean
  server?: boolean
  deep?: boolean
}) => {
  const pzn = computed(() => toValue(_pzn))

  const key = computed(() => constructProductKey({
    pzn: pzn.value,
    additionalDetails: true,
  }))

  const { data: cachedProductDetails } = useNuxtData<AdditionalProductDetails>(key.value)

  const { data, ...rest } = await useAsyncData(key.value, async () => {
    const { data, success, message } = await getAdditionalProductDetails(pzn.value)

    if (!success) {
      throw new Error(message || 'Error fetching product details')
    }

    return data
  }, {
    immediate: opts?.immediate ?? true,
    lazy: opts?.lazy ?? false,
    server: opts?.server ?? true,
    deep: opts?.deep ?? false,
    dedupe: 'cancel',
    getCachedData: getCachedData<AdditionalProductDetails>,
  })

  const productAdditionalDetails = computed(() => data.value as AdditionalProductDetails)

  const clearCachedDetails = () => clearNuxtData(key.value)
  const refreshCachedDetails = () => refreshNuxtData(key.value)

  return {
    productAdditionalDetails,
    // => not really needed, in case we pass immediate=false (and not already use getCachedData),
    // returned data should already be cached one by default (if already fetched)
    cachedProductDetails,
    clearCachedDetails,
    refreshCachedDetails,
    ...rest,
  }
}
